import {
  useFlag,
  useOpenFeatureClientStatus,
  type ReactFlagEvaluationOptions,
} from '@openfeature/react-sdk';
import type { EvaluationDetails, FlagValue } from '@openfeature/core';
import { ProviderStatus } from '@openfeature/web-sdk';
import type { JsonValue } from '@openfeature/web-sdk';

type ResolvedFlagValue<T extends FlagValue> = T extends boolean
  ? boolean
  : T extends number
    ? number
    : T extends string
      ? string
      : T extends JsonValue
        ? T
        : JsonValue;

export function useFeatureFlag<T extends FlagValue>(
  flagKey: string,
  defaultValue: T,
  options?: ReactFlagEvaluationOptions,
): ResolvedFlagValue<T> {
  const evaluation = useFlag(flagKey, defaultValue, options);
  const providerStatus = useOpenFeatureClientStatus();
  const shouldUseDefaultValue =
    evaluation.isError ||
    providerStatus === ProviderStatus.ERROR ||
    providerStatus === ProviderStatus.FATAL ||
    providerStatus === ProviderStatus.STALE;

  return (
    shouldUseDefaultValue ? defaultValue : evaluation.value
  ) as ResolvedFlagValue<T>;
}

export function useFeatureFlagDetails<T extends FlagValue>(
  flagKey: string,
  defaultValue: T,
  options?: ReactFlagEvaluationOptions,
): EvaluationDetails<ResolvedFlagValue<T>> {
  return useFlag(flagKey, defaultValue, options)
    .details as EvaluationDetails<ResolvedFlagValue<T>>;
}
